import type { CreateObjectPhotoGridItem } from 'entities/kyrgyzstan/ui/create-object'

import type { CreateObjectPhoto } from 'shared/api/kyrgyzstan/create-object'

export const CREATE_OBJECT_PHOTOS_MIN_COUNT = 3
export const CREATE_OBJECT_PHOTOS_MAX_COUNT = 30
export const CREATE_OBJECT_PHOTO_MAX_SIZE_MB = 15
export const CREATE_OBJECT_PHOTO_MAX_SIZE_BYTES =
  CREATE_OBJECT_PHOTO_MAX_SIZE_MB * 1024 * 1024
export const CREATE_OBJECT_PHOTO_FORMATS_LABEL = 'JPG, PNG'

const CREATE_OBJECT_PHOTO_ALLOWED_TYPES = ['image/jpeg', 'image/png']

export enum CreateObjectPhotoValidationError {
  UnsupportedType = 'unsupported_type',
  MaxSize = 'max_size',
  MaxCount = 'max_count',
}

interface ValidateCreateObjectPhotoFilesParams {
  files: File[]
  currentPhotosCount: number
}

interface ValidateCreateObjectPhotoFilesResult {
  files: File[]
  error: CreateObjectPhotoValidationError | null
}

export const validateCreateObjectPhotoFiles = ({
  files,
  currentPhotosCount,
}: ValidateCreateObjectPhotoFilesParams): ValidateCreateObjectPhotoFilesResult => {
  if (!files.length) {
    return {
      files: [],
      error: null,
    }
  }

  if (currentPhotosCount + files.length > CREATE_OBJECT_PHOTOS_MAX_COUNT) {
    return {
      files: [],
      error: CreateObjectPhotoValidationError.MaxCount,
    }
  }

  const hasUnsupportedType = files.some(
    (file) => !CREATE_OBJECT_PHOTO_ALLOWED_TYPES.includes(file.type),
  )

  if (hasUnsupportedType) {
    return {
      files: [],
      error: CreateObjectPhotoValidationError.UnsupportedType,
    }
  }

  const hasTooLargeFile = files.some(
    (file) => file.size > CREATE_OBJECT_PHOTO_MAX_SIZE_BYTES,
  )

  if (hasTooLargeFile) {
    return {
      files: [],
      error: CreateObjectPhotoValidationError.MaxSize,
    }
  }

  return {
    files,
    error: null,
  }
}

export const mapCreateObjectPhotoToGridItem = (
  photo: CreateObjectPhoto,
): CreateObjectPhotoGridItem => ({
  id: photo.id,
  src: photo.url,
  alt: '',
})

const normalizeCreateObjectPhotos = (photos: CreateObjectPhoto[]) =>
  photos
    .map((photo) => ({
      id: photo.id,
      url: photo.url,
      sort_order: photo.sort_order,
      is_cover: photo.is_cover,
    }))
    .sort((left, right) => {
      if (left.sort_order !== right.sort_order) {
        return left.sort_order - right.sort_order
      }

      return left.id.localeCompare(right.id)
    })

export const areCreateObjectPhotosEqual = (
  leftPhotos: CreateObjectPhoto[],
  rightPhotos: CreateObjectPhoto[],
) =>
  JSON.stringify(normalizeCreateObjectPhotos(leftPhotos)) ===
  JSON.stringify(normalizeCreateObjectPhotos(rightPhotos))
